// Finding links in message text. The web app turns them into links and the room previews them, so both use this
// one definition. A link wrapped in <…> is still a link, but gets no preview (Discord's convention).

export type TextPart = { kind: "text"; text: string } | { kind: "link"; text: string; href: string; quiet: boolean };

// http(s)://… or www.…, up to whitespace or a direction control (U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069:
// a native client's copy of a link has U+2069 after it, #436); trailing punctuation is trimmed afterwards.
const LINK = /<?(?:https?:\/\/|www\.)[^\s<>"\u200E\u200F\u202A-\u202E\u2066-\u2069]+>?/gi;

/** Splits text into plain runs and links. */
export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK)) {
    let raw = m[0];
    let start = m.index!;
    let quiet = false;
    let wrapped = 0;
    if (raw.startsWith("<") && raw.endsWith(">")) {
      // <link>: shown without the brackets, no preview.
      quiet = true;
      raw = raw.slice(1, -1);
      wrapped = 1;
    } else {
      if (raw.startsWith("<")) {
        raw = raw.slice(1);
        start += 1;
      }
      if (raw.endsWith(">")) raw = raw.slice(0, -1);
      raw = trimTrailing(raw);
    }
    const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
    let ok = false;
    try {
      const u = new URL(href);
      ok = (u.protocol === "http:" || u.protocol === "https:") && (u.hostname.includes(".") || u.hostname === "localhost");
    } catch {
      ok = false;
    }
    if (!ok) continue;
    if (start > last) parts.push({ kind: "text", text: text.slice(last, start) });
    parts.push({ kind: "link", text: raw, href, quiet });
    last = start + raw.length + 2 * wrapped;
  }
  if (last < text.length) parts.push({ kind: "text", text: text.slice(last) });
  return parts;
}

/** Drops sentence punctuation after a link, keeping a closing bracket that has its opening one inside the link. */
function trimTrailing(s: string): string {
  for (;;) {
    const c = s.at(-1);
    if (!c) return s;
    if (".,;:!?'\"*_~".includes(c)) {
      s = s.slice(0, -1);
      continue;
    }
    const pairs: Record<string, string> = { ")": "(", "]": "[", "}": "{" };
    if (pairs[c]) {
      const opens = s.split(pairs[c]).length - 1;
      const closes = s.split(c).length - 1;
      if (closes > opens) {
        s = s.slice(0, -1);
        continue;
      }
    }
    return s;
  }
}

// Which links get previews (not in code or spoilers) is decided by the formatter: see previewLinks in markdown.ts.

/** Where a message link points: a message in a channel or DM (a thread reply's link names its thread's channel). */
export interface MessageLinkRef {
  serverId: string;
  channelId: string;
  messageId: string;
}

/** The path of a link to a message (Copy message link): /s/<server>/<channel>/<message>. */
export function messageLinkPath(r: MessageLinkRef): string {
  return `/s/${r.serverId}/${r.channelId}/${r.messageId}`;
}

const ID = "[A-Za-z0-9_-]{8,64}";
const MESSAGE_PATH = new RegExp(`^/s/(${ID})/(${ID})/(${ID})/?$`);

/**
 * A link to a message on Bloom at `origin` (the app's own origin), or null for anything else. Only the path counts
 * (a query or fragment is ignored), and only on exactly that origin.
 */
export function parseMessageLink(href: string, origin: string): MessageLinkRef | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  if (u.origin !== new URL(origin).origin) return null;
  const m = MESSAGE_PATH.exec(u.pathname);
  // The app's own pages under a server (settings, apps, blooming threads) aren't conversations.
  if (!m || ["settings", "apps", "bloom"].includes(m[2]!)) return null;
  return { serverId: m[1]!, channelId: m[2]!, messageId: m[3]! };
}
